/**
 * The currencies a company may add, and how each is written (SET1-046).
 *
 * The reference's currency form is a pick: choose `Indian Rupee` and the
 * code, symbol, scale, format and units follow. Typing them by hand is how a
 * company ends up with `Rs.` on one record and `₹` on another, or a dinar
 * printed with two decimals where it has three.
 */
export interface CurrencyCatalogueEntry {
  readonly code: string
  readonly name: string
  readonly symbol: string
  readonly symbolPosition: 'LEFT' | 'RIGHT'
  /** Decimal places the currency is written to. */
  readonly scale: number
  /** INDIAN groups 1,23,456.00; WESTERN 123,456.00. */
  readonly grouping: 'INDIAN' | 'WESTERN'
  readonly majorUnit: string
  readonly minorUnit: string
}

const c = (
  code: string, name: string, symbol: string, majorUnit: string, minorUnit: string,
  scale = 2, grouping: 'INDIAN' | 'WESTERN' = 'WESTERN', symbolPosition: 'LEFT' | 'RIGHT' = 'LEFT',
): CurrencyCatalogueEntry => ({ code, name, symbol, symbolPosition, scale, grouping, majorUnit, minorUnit })

export const CURRENCY_CATALOGUE: readonly CurrencyCatalogueEntry[] = [
  c('INR', 'Indian Rupee', '₹', 'Rupee', 'Paise', 2, 'INDIAN'),
  c('USD', 'US Dollar', '$', 'Dollar', 'Cent'),
  c('EUR', 'Euro', '€', 'Euro', 'Cent'),
  c('GBP', 'Pound Sterling', '£', 'Pound', 'Penny'),
  c('AED', 'UAE Dirham', 'AED', 'Dirham', 'Fils'),
  c('SAR', 'Saudi Riyal', 'SAR', 'Riyal', 'Halala'),
  c('QAR', 'Qatari Riyal', 'QAR', 'Riyal', 'Dirham'),
  c('OMR', 'Omani Rial', 'OMR', 'Rial', 'Baisa', 3),
  c('KWD', 'Kuwaiti Dinar', 'KWD', 'Dinar', 'Fils', 3),
  c('BHD', 'Bahraini Dinar', 'BHD', 'Dinar', 'Fils', 3),
  c('SGD', 'Singapore Dollar', 'S$', 'Dollar', 'Cent'),
  c('MYR', 'Malaysian Ringgit', 'RM', 'Ringgit', 'Sen'),
  c('THB', 'Thai Baht', '฿', 'Baht', 'Satang'),
  c('HKD', 'Hong Kong Dollar', 'HK$', 'Dollar', 'Cent'),
  c('CNY', 'Chinese Yuan', '¥', 'Yuan', 'Fen'),
  c('JPY', 'Japanese Yen', '¥', 'Yen', 'Sen', 0),
  c('AUD', 'Australian Dollar', 'A$', 'Dollar', 'Cent'),
  c('NZD', 'New Zealand Dollar', 'NZ$', 'Dollar', 'Cent'),
  c('CAD', 'Canadian Dollar', 'C$', 'Dollar', 'Cent'),
  c('CHF', 'Swiss Franc', 'CHF', 'Franc', 'Rappen'),
  c('ZAR', 'South African Rand', 'R', 'Rand', 'Cent'),
  c('LKR', 'Sri Lankan Rupee', 'Rs', 'Rupee', 'Cent'),
  c('NPR', 'Nepalese Rupee', 'Rs', 'Rupee', 'Paisa'),
  c('BDT', 'Bangladeshi Taka', '৳', 'Taka', 'Poisha'),
]

const byName = new Map(CURRENCY_CATALOGUE.map((x) => [x.name.toLowerCase(), x]))
const byCode = new Map(CURRENCY_CATALOGUE.map((x) => [x.code, x]))

/** The catalogue entry a currency record names, by name or, failing that, by code. */
export const currencyFromCatalogue = (name: unknown, code?: unknown): CurrencyCatalogueEntry | undefined =>
  (typeof name === 'string' ? byName.get(name.trim().toLowerCase()) : undefined)
  ?? (typeof code === 'string' ? byCode.get(code.trim().toUpperCase()) : undefined)
